import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import type { SearchHit, SearchResults, SearchUnitHit } from "../api/types";
import { ARTICLE_ROLES, ArticleUnitRow } from "../components/ArticleUnitRow";
import { StatusBadge } from "../components/StatusBadge";
import { FileText, Search as SearchIcon } from "../compose/Icon";
import { EmptyState, ErrorState, SkeletonRows } from "../compose/states";
import { highlightSegments } from "../lib/highlight";
import { useAsync } from "../lib/useAsync";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import "../styles/search.css";

/**
 * Global search `/w/:slug/search` — ARTICLE-UNIT-FIRST (IA sweep · design §5). A
 * debounced query box searches document titles + rendered body text across the
 * workspace (server-side, tenant-scoped). The BACKEND groups the hits: a match in
 * ANY of an article unit's current-head resources (brief/research/article)
 * surfaces the UNIT ONCE — never one row per resource, and never the superseded
 * version history. Matched units render with the SAME `<ArticleUnitRow>` card the
 * Articles landing uses (title + brief/research/article chips + status), drilling
 * straight into the workbench (`/w/:slug/article/:id`). Loose / legacy documents
 * (no owning unit) collapse into a secondary "Other documents" section that
 * drills into the flat reader (`/doc/:id`). The settled query is URL-synced (?q=)
 * so a search stays shareable.
 *
 * RULE ZERO: the unit card is the shared `<ArticleUnitRow>` (one list look), the
 * status pill is `<StatusBadge>`, and matches are lit with the shared
 * `highlightSegments` — no new list surface, no second highlighter.
 */

function roleLabel(role: string): string {
  return ARTICLE_ROLES.find((r) => r.key === role)?.label ?? "Document";
}

/** Split the backend's comma-joined `matched_roles` into labels. */
function matchedRoleLabels(matched: string): string[] {
  return matched
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean)
    .map(roleLabel);
}

export function Search() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get("q") ?? "");
  const debounced = useDebouncedValue(term.trim(), 250);

  const onChange = (next: string) => {
    setTerm(next);
    const p = new URLSearchParams(params);
    if (next.trim()) p.set("q", next.trim());
    else p.delete("q");
    setParams(p, { replace: true });
  };

  const results = useAsync<SearchResults>(
    () =>
      debounced
        ? api.search(slug, debounced)
        : Promise.resolve({ units: [], other: [] }),
    [slug, debounced],
  );

  const data = results.data ?? { units: [], other: [] };
  const count = data.units.length + data.other.length;

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Search</h1>
          <p className="page__sub">
            Find an article unit — or any document — by title or body text across
            this workspace.
          </p>
        </div>
      </div>

      <div className="searchbox">
        <span className="searchbox__icon" aria-hidden>
          <SearchIcon size={17} strokeWidth={1.5} />
        </span>
        <input
          type="search"
          className="searchbox__input"
          placeholder="Search articles…"
          aria-label="Search articles"
          data-testid="search-input"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          value={term}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>

      {!debounced ? (
        <EmptyState
          icon={<SearchIcon size={30} strokeWidth={1.5} />}
          title="Search articles"
          body="Type a word or phrase to search article units — their brief, research and article — plus any loose document, across this workspace."
        />
      ) : results.loading ? (
        <div className="doclist">
          <SkeletonRows rows={3} />
        </div>
      ) : results.error ? (
        <ErrorState body="We couldn't run that search. Try again." />
      ) : count > 0 ? (
        <>
          <p className="page__sub search-count" data-testid="search-count">
            {count} result{count === 1 ? "" : "s"} for “{debounced}”
          </p>

          {data.units.length > 0 ? (
            <div className="doclist" data-testid="search-results">
              {data.units.map((unit) => (
                <SearchUnitResult
                  key={`u-${unit.id}`}
                  slug={slug}
                  unit={unit}
                  term={debounced}
                />
              ))}
            </div>
          ) : null}

          {data.other.length > 0 ? (
            <details className="search-other" data-testid="search-other">
              <summary
                className="search-other__summary"
                data-testid="search-other-toggle"
              >
                Other documents
                <span className="search-other__count">{data.other.length}</span>
              </summary>
              <div className="doclist" data-testid="search-other-results">
                {data.other.map((hit) => (
                  <ResultRow
                    key={hit.id}
                    slug={slug}
                    hit={hit}
                    term={debounced}
                  />
                ))}
              </div>
            </details>
          ) : null}
        </>
      ) : (
        <EmptyState
          icon={<FileText size={30} strokeWidth={1.5} />}
          title="No matches"
          body={`Nothing in this workspace matches “${debounced}”. Try a different word.`}
        />
      )}
    </div>
  );
}

function Highlighted({ text, term }: { text: string; term: string }) {
  const segments = useMemo(() => highlightSegments(text, term), [text, term]);
  return (
    <>
      {segments.map((s, i) =>
        s.match ? (
          <mark key={i} className="search-mark">
            {s.text}
          </mark>
        ) : (
          <span key={i}>{s.text}</span>
        ),
      )}
    </>
  );
}

/**
 * A matched ARTICLE UNIT — rendered with the shared `<ArticleUnitRow>` card
 * (title + brief/research/article chips + status), drilling into
 * `/w/:slug/article/:id`. The `footer` slot carries the search extras: the
 * matched-resource snippet + a "matched in …" hint (which resources hit the
 * query, since the chips reflect the unit's full composition, not the match).
 */
export function SearchUnitResult({
  slug,
  unit,
  term,
}: {
  slug: string;
  unit: SearchUnitHit;
  term: string;
}) {
  const matched = matchedRoleLabels(unit.matched_roles ?? "");
  const footer = (
    <>
      {unit.snippet ? (
        <div
          className="search-snippet"
          data-testid={`search-snippet-${unit.id}`}
        >
          <Highlighted text={unit.snippet} term={term} />
        </div>
      ) : null}
      {matched.length > 0 ? (
        <div
          className="search-unit__matched"
          data-testid={`search-matched-${unit.id}`}
        >
          Matched in {matched.join(", ")}
        </div>
      ) : null}
    </>
  );

  return (
    <ArticleUnitRow
      slug={slug}
      unit={unit}
      rowTestId={`search-unit-${unit.id}`}
      rolesTestId={`search-unit-roles-${unit.id}`}
      footer={footer}
    />
  );
}

/** A loose / legacy document hit — the flat reader row (`/doc/:id`). */
export function ResultRow({
  slug,
  hit,
  term,
}: {
  slug: string;
  hit: SearchHit;
  term: string;
}) {
  return (
    <Link
      to={`/w/${slug}/doc/${hit.id}`}
      className="docrow"
      data-testid={`search-other-${hit.id}`}
    >
      <div className="docrow__main">
        <div className="docrow__title">
          <Highlighted text={hit.title} term={term} />
        </div>
        {hit.snippet ? (
          <div className="search-snippet" data-testid={`search-snippet-${hit.id}`}>
            <Highlighted text={hit.snippet} term={term} />
          </div>
        ) : null}
        <div className="docrow__meta">
          <span>{hit.folder || "Unfiled"}</span>
        </div>
      </div>
      <div className="docrow__right">
        <StatusBadge status={hit.status} />
      </div>
    </Link>
  );
}
